import dayjs from "dayjs";
import {
  extractStockTaxRows,
  extractStockTaxSummaryTotal,
  parseStockTaxReport,
  STOCK_TAX_INTRADAY_REPORT,
} from "../../../utils/equity/stockTaxReport";
import { isEquitySellType } from "../../../utils/reports/equityTransactionTypes";
import {
  forceBlankInvestedAmountFields,
  formatReportTypeColumnLabel,
  resolveReportSaleAmount,
} from "../../../utils/reports/reportDisplayLabels";
import { withTxnBenchmarkFields, pickHoldingBenchmarkFields } from "../../../utils/reports/transactionBenchmarkColumns";
import { withTxnMetadataFields } from "../../../utils/reports/transactionMetadataColumns";
import {
  attachInvestorMeta,
  buildUserMetaFromInvestors,
  parseHoldingsDateRangeTimestamps,
  type UserMeta,
} from "../mf/mfReportParsing";
import { EQUITY_POINT_TO_POINT_REPORT } from "./equityReportConfig";

export { attachInvestorMeta, buildUserMetaFromInvestors, type UserMeta };

const formatEpochDate = (value: unknown): string => {
  if (value == null || value === "" || value === "-") return "-";
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) {
    return dayjs(n).format("DD/MM/YYYY");
  }
  return String(value);
};

const resolveCurrentDateOrToday = (row: Record<string, any>): string => {
  const meta = row?.metadata ?? row?.meta ?? row?.metaData ?? {};
  const candidates = [row?.currentDate, row?.current_date, meta?.currentDate];
  for (const candidate of candidates) {
    if (
      candidate == null ||
      candidate === "" ||
      candidate === "-" ||
      candidate === 0 ||
      candidate === "0"
    ) {
      continue;
    }
    const formatted = formatEpochDate(candidate);
    if (formatted && formatted !== "-") return formatted;
  }
  return dayjs().format("DD/MM/YYYY");
};

const getHoldingSchemeName = (row: Record<string, any>): string =>
  String(row.symbol ?? row.schemeName ?? row.name ?? "-");

const getHoldingTransactionList = (holding: Record<string, any>): any[] => {
  for (const key of [
    "transactions",
    "txns",
    "transactionDetails",
    "transactionList",
    "transaction",
    "trxn",
  ] as const) {
    const list = holding[key];
    if (Array.isArray(list) && list.length > 0) return list;
  }
  return [];
};

const isSellTxn = (row: Record<string, any>): boolean => {
  const raw = String(
    row.transType ?? row.txnType ?? row.transactionType ?? row.transactiontype ?? "",
  ).trim();
  if (!raw) return false;
  if (isEquitySellType(raw)) return true;
  const txnType = raw.toLowerCase();
  return (
    txnType.includes("sell") ||
    txnType.includes("redemption") ||
    txnType.includes("redeem")
  );
};

const filterPositiveUnits = (rows: any[]) =>
  rows.filter((r) => Number(r?.units ?? r?.quantity ?? 0) > 0);

const filterRealizedTxnRows = (rows: any[]) =>
  rows.filter((r) => isSellTxn(r)).map((r) => ({
    ...r,
    type: formatReportTypeColumnLabel("Realized", "Realized"),
    displayTransType: "Realized",
  }));

const normalizeMisMarketValueToCurrentValue = (rows: any[]): any[] =>
  rows.map((row) => {
    if (!row || typeof row !== "object") return row;
    const r = row as Record<string, any>;
    const currentValue =
      r.currentValue ?? r.currentAmount ?? r.marketValue ?? r.market_value;
    return { ...r, currentValue };
  });

const resolveHoldingAverageDays = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? {};
  const val =
    row.averageDays ??
    row.averageDay ??
    row.days ??
    meta.averageDays ??
    meta.days;
  if (val == null || val === "") return "-";
  return val;
};

const resolveHoldingCagr = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? row?.metaData ?? {};
  for (const key of ["cagr", "cagrStock", "cagrPrivateStock"] as const) {
    const val = row[key] ?? meta[key];
    if (val != null && val !== "") return val;
  }
  return row.cagr ?? "-";
};

const normalizeUnrealizedHoldingRow = (row: Record<string, any>) => {
  const meta = row?.metadata ?? row?.meta ?? {};
  const schemeName = getHoldingSchemeName(row);
  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...row,
      schemeName,
      symbol: row.symbol ?? schemeName,
      type: formatReportTypeColumnLabel("Unrealized", "Unrealized"),
      units: row.units ?? row.quantity,
      amount: row.amount ?? row.investedAmount ?? row.costValue,
      currentAmount: row.currentAmount ?? row.currentValue ?? row.marketValue,
      averagePrice:
        row.averagePrice ?? row.avgPrice ?? row.averageNav ?? meta.averagePrice,
      currentPrice:
        row.currentPrice ?? row.currentNAV ?? row.currentNav ?? meta.currentPrice,
      gainLoss:
        row.gainLoss ??
        row.unrealisedPNL ??
        row.unrealizedPNL ??
        row.unrealisedGainLoss,
      gainLossPerc:
        row.gainLossPerc ??
        row.gainLossPercentage ??
        row.unrealisedPNLPercentage ??
        row.unrealizedPNLPercentage,
      purchaseDate: formatEpochDate(row.purchaseDate ?? row.transactionDate),
      currentDate: resolveCurrentDateOrToday(row),
      days: resolveHoldingAverageDays(row),
      cagr: resolveHoldingCagr(row),
      adjustedCostPrice:
        row.adjustedCostPrice ?? meta.adjustedCostPrice ?? row.adjCost,
      isin: row.isin ?? "-",
    }),
  );
};

const normalizeRealizedRow = (row: Record<string, any>) => {
  const meta = row.metadata ?? row.metaData ?? row.meta ?? {};
  const schemeName = getHoldingSchemeName(row);
  const avgBuy =
    row.avgBuyPrice ??
    row.averageBuyPrice ??
    row.avgPurchasePrice ??
    row.averagePrice ??
    row.avgPrice ??
    meta.avgBuyPrice ??
    meta.averageBuyPrice ??
    meta.averagePrice ??
    "-";
  const purchaseNAV =
    row.purchaseNAV ??
    row.purchaseNav ??
    row.purchasePrice ??
    row.nav ??
    meta.purchaseNAV ??
    meta.purchasePrice;
  const saleValue = resolveReportSaleAmount(row) ?? row.saleValue ?? row.amount;
  return forceBlankInvestedAmountFields(
    withTxnMetadataFields(
      withTxnBenchmarkFields({
        ...row,
        schemeName,
        symbol: row.symbol ?? schemeName,
        type: formatReportTypeColumnLabel("Realized", "Realized"),
        displayTransType: "Realized",
        displayTransactionType: "Realized",
        transactionDate: formatEpochDate(
          row.transactionDate ?? row.transactiondate ?? row.date,
        ),
        purchaseDate: formatEpochDate(
          row.purchaseDate ?? meta.purchaseDate ?? row.transactionDate,
        ),
        currentDate: resolveCurrentDateOrToday(row),
        purchaseNAV: purchaseNAV ?? "-",
        avgBuyPrice: avgBuy,
        saleValue,
        realisedPnl:
          row.realisedPnl ??
          row.realisedPnL ??
          row.realisedPNL ??
          row.realizedPnl ??
          row.realisedGainLoss ??
          row.gainLoss,
        realisedPNLPercentage:
          row.realisedPNLPercentage ??
          row.realisedPnLPercentage ??
          row.realizedPNLPercentage ??
          row.gainLossPerc ??
          row.gainLossPercentage,
        isin: row.isin ?? meta.isin ?? "-",
        cagr: resolveHoldingCagr(row),
        gainLoss: undefined,
      }),
    ),
  );
};

const flattenEquityHoldingsTransactions = (holdings: any[]): any[] => {
  const txns: any[] = [];
  for (const h of holdings || []) {
    if (!h || typeof h !== "object") continue;
    const schemeName = getHoldingSchemeName(h);
    const list = getHoldingTransactionList(h);
    if (!list.length) continue;
    const holdingBenchmarks = pickHoldingBenchmarkFields(h);
    for (const t of list) {
      const transType = t.transType ?? t.txnType ?? t.transactionType;
      txns.push(
        withTxnMetadataFields(
          withTxnBenchmarkFields({
            ...holdingBenchmarks,
            ...t,
            schemeName: t.schemeName ?? schemeName,
            symbol: t.symbol ?? h.symbol ?? schemeName,
            isin: t.isin ?? h.isin,
            userId: t.userId ?? h.userId,
            memberName: t.memberName ?? h.memberName,
            transactionDate: t.transactionDate ?? t.transactiondate ?? t.date,
            purchaseDate: t.purchaseDate ?? t.transactionDate ?? t.date,
            currentDate: resolveCurrentDateOrToday({
              ...h,
              ...t,
              currentDate: t.currentDate ?? h.currentDate,
            }),
            transType,
            txnType: t.txnType ?? transType,
            transactionType: t.transactionType ?? transType,
            purchasePrice:
              t.purchasePrice ?? t.price ?? t.avgPrice ?? t.averagePrice,
            purchaseNAV:
              t.purchaseNAV ?? t.purchaseNav ?? t.purchasePrice ?? t.price,
            currentPrice: t.currentPrice ?? t.currentNAV ?? h.currentPrice,
            amount: t.amount ?? t.costValue ?? t.investedAmount,
            currentAmount: t.currentAmount ?? t.currentValue ?? h.currentAmount,
            units: t.units ?? t.quantity,
            days: t.days ?? t.avgDays ?? h.averageDays,
            avgBuyPrice: t.avgBuyPrice ?? t.averageBuyPrice,
            cagr: t.cagr ?? h.cagr,
            realisedPnl:
              t.realisedPnl ??
              t.realisedPnL ??
              t.realisedPNL ??
              t.realizedPnl ??
              t.realizedPNL,
            realisedPNLPercentage:
              t.realisedPNLPercentage ??
              t.realisedPnLPercentage ??
              t.realizedPNLPercentage ??
              t.realizedPnLPercentage,
          }),
        ),
      );
    }
  }
  return txns;
};

const normalizeHoldingRowForAll = (row: Record<string, any>) => {
  const normalized = normalizeUnrealizedHoldingRow({
    ...row,
    type: "Unrealized",
  });
  return {
    ...normalized,
    averageNav: normalized.averagePrice ?? normalized.averageNav,
    currentNAV: normalized.currentPrice ?? normalized.currentNAV,
    costValue: normalized.amount ?? normalized.costValue ?? normalized.investedAmount,
    currentValue:
      normalized.currentAmount ?? normalized.currentValue ?? normalized.marketValue,
    gainLoss: normalized.gainLoss,
    gainLossPercentage:
      normalized.gainLossPerc ?? normalized.gainLossPercentage,
  };
};

const normalizeTxnRowForAll = (row: Record<string, any>) => {
  const isSell = isSellTxn(row);
  const schemeName = getHoldingSchemeName(row);
  const meta = row?.metadata ?? row?.meta ?? {};
  const normalized = normalizeRealizedRow({ ...row, schemeName });
  const averageNav =
    row.purchaseNAV ??
    row.purchaseNav ??
    row.averagePrice ??
    row.avgBuyPrice ??
    row.averageBuyPrice ??
    row.avgPurchasePrice ??
    row.purchasePrice ??
    meta.purchaseNAV ??
    meta.averagePrice ??
    meta.purchasePrice ??
    normalized.purchaseNAV;
  const currentNAV =
    row.currentPrice ??
    row.currentNAV ??
    row.currentNav ??
    meta.currentPrice ??
    meta.currentNAV ??
    normalized.currentPrice ??
    normalized.currentNAV;
  const pnl =
    row.realisedPnl ??
    row.realisedPNL ??
    row.realizedPnl ??
    row.realizedPNL ??
    row.realisedGainLoss ??
    row.realizedGainLoss ??
    normalized.realisedPnl;
  const pnlPct =
    row.realisedPNLPercentage ??
    row.realisedPnLPercentage ??
    row.realizedPNLPercentage ??
    row.realizedPnLPercentage ??
    normalized.realisedPNLPercentage;
  return {
    ...normalized,
    schemeName,
    type: formatReportTypeColumnLabel(
      isSell ? "Realized" : "Unrealized",
      isSell ? "Realized" : "Unrealized",
    ),
    averageNav: averageNav ?? "-",
    currentNAV: currentNAV ?? "-",
    costValue: isSell ? "-" : (row.amount ?? row.costValue),
    currentValue: row.currentValue ?? row.currentAmount ?? row.currentVal ?? "-",
    gainLoss: pnl ?? "-",
    gainLossPercentage: pnlPct ?? "-",
  };
};

const extractStockHoldings = (payload: any): any[] => {
  if (Array.isArray(payload?.holdings)) return payload.holdings;
  if (Array.isArray(payload?.data?.holdings)) return payload.data.holdings;
  if (Array.isArray(payload?.data?.data?.holdings)) return payload.data.data.holdings;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const extractTotalHoldingInfo = (payload: any): Record<string, unknown> | null =>
  payload?.totalHoldingInfo ??
  payload?.totalHolding ??
  payload?.data?.totalHoldingInfo ??
  null;

const isP2pSellRow = (row: Record<string, any>): boolean => {
  if (
    row.sellingAmount != null ||
    row.sellingNav != null ||
    row.sellingNAV != null
  ) {
    return true;
  }
  const rawLabel = String(
    row.transType ?? row.txnType ?? row.transactionType ?? row.transactiontype ?? "",
  ).trim();
  if (isEquitySellType(rawLabel)) return true;
  const raw = rawLabel.replace(/\s+/g, "_").toUpperCase();
  return raw.includes("SELL") || raw.includes("REDEEM") || raw === "REDEMPTION";
};

/** Mirrors aumsampaan `mapStockP2pReportRow`. */
const mapStockP2pReportRow = (row: Record<string, any>): Record<string, any> => {
  const isSell = isP2pSellRow(row);
  const unrealPnlRaw =
    row.unrealisedPNL ?? row.unrealisedPnL ?? row.unrealizedPNL;
  const unrealPercRaw =
    row.unrealisedPNLPercentage ??
    row.unrealisedPnLPercentage ??
    row.unrealizedPNLPercentage;
  const realisedPnlRaw =
    row.realisedPnl ?? row.realisedPNL ?? row.realizedPnl ?? row.realizedPNL;
  const realisedPercRaw =
    row.realisedPNLPercentage ??
    row.realisedPnLPercentage ??
    row.realizedPNLPercentage;
  return withTxnBenchmarkFields({
    ...row,
    symbol: row.symbol ?? row.schemeName ?? "-",
    exchange: row.exchange ?? "-",
    transType: isSell ? "Sell" : "Buy",
    units: row.units ?? row.quantity,
    investedAmount: isSell
      ? (row.sellingAmount ?? row.amount)
      : (row.investedAmount ?? row.amount),
    currentAmount: row.currentAmount ?? row.currentValue,
    avgPrice: row.avgPrice ?? row.purchaseNAV ?? row.purchaseNav,
    currentNAV: isSell
      ? (row.sellingNav ?? row.sellingNAV ?? row.currentNAV ?? row.currentNav)
      : (row.currentNAV ?? row.currentNav),
    xirr: row.xirr ?? "-",
    cagr: row.cagr ?? "-",
    unrealisedPNL: isSell ? "-" : (unrealPnlRaw ?? "-"),
    unrealisedPNLPercentage: isSell ? "-" : (unrealPercRaw ?? "-"),
    realisedPnl: isSell ? (realisedPnlRaw ?? "-") : "-",
    realisedPNL: isSell ? (realisedPnlRaw ?? "-") : "-",
    realisedPNLPercentage: isSell ? (realisedPercRaw ?? "-") : "-",
  });
};

const extractEquityP2pRows = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.response)) return payload.response;
  return [];
};

const extractP2pTotalHoldingInfo = (payload: any): Record<string, unknown> | null => {
  const cagr =
    payload?.cagr ?? payload?.data?.cagr ?? payload?.data?.data?.cagr ?? null;
  if (cagr === null || cagr === undefined) return null;
  return { cagr };
};

const extractTaxReportPayload = (payload: any) =>
  payload?.response ?? payload?.data?.response ?? payload?.data ?? payload;

export type ParsedEquityReport = {
  rows: any[];
  totalHoldingInfo?: Record<string, unknown> | null;
  totalPages?: number;
  pageIndex?: number;
  totalRecords?: number;
  taxTotalGainLoss?: number | null;
};

export const parseEquityReportResponse = (
  reportType: string,
  payload: any,
  userMeta: UserMeta,
  selectedUserIds: number[] = [],
): ParsedEquityReport => {
  let rows: any[] = [];
  let totalHoldingInfo: Record<string, unknown> | null = null;
  let totalPages = 1;
  let pageIndex = 1;
  let totalRecords = 0;
  let taxTotalGainLoss: number | null = null;

  switch (reportType) {
    case "Holding": {
      const holdings = extractStockHoldings(payload);
      rows = filterPositiveUnits(holdings).map(normalizeUnrealizedHoldingRow);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      break;
    }
    case "Realized": {
      const holdings = extractStockHoldings(payload);
      rows = filterRealizedTxnRows(flattenEquityHoldingsTransactions(holdings)).map(
        normalizeRealizedRow,
      );
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    case "All": {
      const holdings = extractStockHoldings(payload);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      const unrealized = filterPositiveUnits(holdings).map(normalizeHoldingRowForAll);
      const realized = filterRealizedTxnRows(
        flattenEquityHoldingsTransactions(holdings),
      ).map(normalizeTxnRowForAll);
      rows = [...unrealized, ...realized];
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    case "Transaction": {
      const holdings = extractStockHoldings(payload);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      rows = flattenEquityHoldingsTransactions(holdings);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    case "MIS Report": {
      const raw = Array.isArray(payload) ? payload : payload?.data ?? payload ?? [];
      rows = normalizeMisMarketValueToCurrentValue(raw);
      rows = [...rows].sort(
        (a, b) => Number(b.absoluteReturnPerc ?? 0) - Number(a.absoluteReturnPerc ?? 0),
      );
      break;
    }
    case EQUITY_POINT_TO_POINT_REPORT: {
      rows = extractEquityP2pRows(payload).map(mapStockP2pReportRow);
      totalHoldingInfo = extractP2pTotalHoldingInfo(payload);
      break;
    }
    case STOCK_TAX_INTRADAY_REPORT:
    case "Short Term Capital Gain":
    case "Long Term Capital Gain": {
      const parsed = parseStockTaxReport(extractTaxReportPayload(payload));
      rows = parsed ? extractStockTaxRows(parsed, reportType) : [];
      taxTotalGainLoss = extractStockTaxSummaryTotal(parsed, reportType);
      break;
    }
    default:
      rows = [];
  }

  return {
    rows: attachInvestorMeta(rows, userMeta, selectedUserIds),
    totalHoldingInfo,
    totalPages,
    pageIndex,
    totalRecords,
    taxTotalGainLoss,
  };
};

export const isEquityP2pDateRangeComplete = parseHoldingsDateRangeTimestamps;

export const mergeEquityTransactionPages = (existing: any[], next: any[]) => {
  const seen = new Set(
    existing.map(
      (r) =>
        `${r.userId}-${r.symbol ?? r.schemeName}-${r.transactionDate}-${r.units}-${r.transType}`,
    ),
  );
  const merged = [...existing];
  for (const row of next) {
    const key = `${row.userId}-${row.symbol ?? row.schemeName}-${row.transactionDate}-${row.units}-${row.transType}`;
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(row);
    }
  }
  return merged;
};
